import { useEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import {
  ArrowDownUp,
  ChevronDown,
  CircleCheck,
  Columns3,
  Layers,
  ListFilter,
  Search,
  Settings2,
  Split,
  UsersRound,
  X,
  Plus,
} from '@/components/ui/icons';
import type { GroupBy, Location, View, ViewSettings } from '@shared/types';
import { BUILTIN_FIELDS } from '@shared/constants';
import { useDb, useMe } from '@/lib/store';
import { fieldsForLocation } from '@/lib/hierarchy';
import { updateViewSettings } from '@/lib/actions';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui/Menu';
import { Tooltip } from '@/components/ui/Tooltip';
import { Avatar } from '@/components/ui/Avatar';
import { PeopleMenu } from '@/components/fields/People';
import { FilterPanel } from './FilterPanel';
import { FieldsPanel } from './FieldsPanel';

const GROUP_LABEL: Record<string, string> = {
  status: 'Status',
  assignee: 'Assignee',
  priority: 'Priority',
  dueDate: 'Due date',
  taskType: 'Task Type',
  tags: 'Tags',
  list: 'List',
  none: 'None',
};

export function groupLabel(g: GroupBy, cfName?: string) {
  if (g.startsWith('cf:')) return cfName ?? 'Field';
  return GROUP_LABEL[g] ?? g;
}

export interface ToolbarProps {
  view: View;
  location: Location;
  search: string;
  onSearch: (s: string) => void;
  onAddTask: () => void;
  /** which controls to show */
  features?: { group?: boolean; subtasks?: boolean; columns?: boolean; sort?: boolean };
  leftExtra?: ReactNode;
  rightExtra?: ReactNode;
}

export function ViewToolbar({ view, location, search, onSearch, onAddTask, features = { group: true, subtasks: true, columns: true, sort: true }, leftExtra, rightExtra }: ToolbarProps) {
  const s = view.settings;
  const set = (changes: Partial<ViewSettings>) => updateViewSettings(view.id, changes);
  const db = useDb((d) => d);
  const me = useMe();
  const fields = fieldsForLocation(db, location);
  const groupable = fields.filter((f) => f.type === 'dropdown' || f.type === 'labels' || f.type === 'checkbox');
  const cfGroupName = s.groupBy.startsWith('cf:') ? db.customFields[s.groupBy.slice(3)]?.name : undefined;
  const [searchOpen, setSearchOpen] = useState(!!search);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);
  const assigneeRule = s.filters.rules.find((r) => r.id === 'quick-assignee');
  const assigneeIds = (assigneeRule?.value as string[] | undefined) ?? [];

  return (
    <div className="view-toolbar">
      <div className="row gap-4">
        {features.group && (
          <Popover
            content={(close) => (
              <Menu style={{ width: 240 }}>
                <MenuLabel>Group by</MenuLabel>
                {(['status', 'assignee', 'priority', 'dueDate', 'taskType', 'tags', ...(location.type !== 'list' ? ['list'] : [])] as GroupBy[]).map((g) => (
                  <MenuItem key={g} label={GROUP_LABEL[g]} checked={s.groupBy === g} onClick={() => (set({ groupBy: g, collapsedGroups: [] }), close())} />
                ))}
                {groupable.length > 0 && <MenuSeparator />}
                {groupable.map((f) => (
                  <MenuItem key={f.id} label={f.name} checked={s.groupBy === `cf:${f.id}`} onClick={() => (set({ groupBy: `cf:${f.id}`, collapsedGroups: [] }), close())} />
                ))}
                <MenuSeparator />
                <MenuItem label="None" checked={s.groupBy === 'none'} onClick={() => (set({ groupBy: 'none' }), close())} />
                <MenuSeparator />
                <MenuItem icon={<ArrowDownUp size={15} />} label={s.groupDir === 'asc' ? 'Ascending' : 'Descending'} onClick={() => set({ groupDir: s.groupDir === 'asc' ? 'desc' : 'asc' })} />
              </Menu>
            )}
          >
            <button className={clsx('chip', s.groupBy !== 'none' && 'accent')}>
              <Layers size={13} />
              {s.groupBy === 'none' ? 'Group' : groupLabel(s.groupBy, cfGroupName)}
            </button>
          </Popover>
        )}
        {features.subtasks && (
          <Popover
            content={(close) => (
              <Menu style={{ width: 240 }}>
                <MenuLabel>Show subtasks</MenuLabel>
                <MenuItem label="Collapse all" checked={s.subtasks === 'collapsed'} onClick={() => (set({ subtasks: 'collapsed' }), close())} />
                <MenuItem label="Expand all" checked={s.subtasks === 'expanded'} onClick={() => (set({ subtasks: 'expanded' }), close())} />
                <MenuItem label="As separate tasks" checked={s.subtasks === 'separate'} onClick={() => (set({ subtasks: 'separate' }), close())} />
              </Menu>
            )}
          >
            <Tooltip label="Subtasks">
              <button className={clsx('icon-btn sm', s.subtasks !== 'collapsed' && 'active')}>
                <Split size={15} style={{ transform: 'rotate(90deg)' }} />
              </button>
            </Tooltip>
          </Popover>
        )}
        {features.columns && (
          <Popover placement="bottom-start" content={() => <FieldsPanel location={location} settings={s} onChange={set} />}>
            <Tooltip label="Columns">
              <button className="icon-btn sm">
                <Columns3 size={15} />
              </button>
            </Tooltip>
          </Popover>
        )}
        {leftExtra}
        {s.sort.length > 0 && (
          <span className="chip">
            <ArrowDownUp size={12} />
            Sort: {s.sort.map((x) => (x.field.startsWith('cf:') ? db.customFields[x.field.slice(3)]?.name : BUILTIN_FIELDS.find((f) => f.id === x.field)?.label)).join(', ')}
            <button onClick={() => set({ sort: [] })}>
              <X size={12} />
            </button>
          </span>
        )}
      </div>
      <div className="row gap-4">
        {rightExtra}
        <Popover placement="bottom-end" content={() => <FilterPanel location={location} settings={s} onChange={set} />}>
          <button className={clsx('toolbar-btn', s.filters.rules.length > 0 && 'on')}>
            <ListFilter size={15} />
            {s.filters.rules.length > 0 ? <span>{s.filters.rules.length}</span> : null}
          </button>
        </Popover>
        <Tooltip label={s.showClosed ? 'Hide closed' : 'Show closed'}>
          <button className={clsx('toolbar-btn', s.showClosed && 'on')} onClick={() => set({ showClosed: !s.showClosed })}>
            <CircleCheck size={15} />
          </button>
        </Tooltip>
        <Popover
          placement="bottom-end"
          content={() => (
            <PeopleMenu
              title="Assignees"
              selected={assigneeIds}
              onChange={(ids) => {
                const rest = s.filters.rules.filter((r) => r.id !== 'quick-assignee');
                set({ filters: { ...s.filters, rules: ids.length ? [...rest, { id: 'quick-assignee', field: 'assignee', operator: 'any', value: ids }] : rest } });
              }}
            />
          )}
        >
          <Tooltip label="Assignee">
            <button className={clsx('toolbar-btn', assigneeIds.length > 0 && 'on')}>
              <UsersRound size={15} />
              {assigneeIds.length > 0 && <span>{assigneeIds.length}</span>}
            </button>
          </Tooltip>
        </Popover>
        <Tooltip label="Me mode: show only tasks assigned to me">
          <button className={clsx('toolbar-btn me-mode', s.meMode && 'on')} onClick={() => set({ meMode: !s.meMode })}>
            <Avatar userId={me} size={18} />
            {s.meMode && <span>Me mode</span>}
          </button>
        </Tooltip>
        {searchOpen ? (
          <div className="toolbar-search">
            <Search size={14} className="faint" />
            <input
              ref={searchRef}
              value={search}
              placeholder="Search tasks..."
              onChange={(e) => onSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  onSearch('');
                  setSearchOpen(false);
                }
              }}
              onBlur={() => !search && setSearchOpen(false)}
            />
            {search && (
              <button className="icon-btn xs" onClick={() => onSearch('')}>
                <X size={12} />
              </button>
            )}
          </div>
        ) : (
          <Tooltip label="Search">
            <button className="toolbar-btn" onClick={() => setSearchOpen(true)}>
              <Search size={15} />
            </button>
          </Tooltip>
        )}
        <Popover
          placement="bottom-end"
          content={() => (
            <Menu style={{ width: 260 }}>
              <MenuLabel>Customize view</MenuLabel>
              <MenuItem label="Show empty statuses" checked={!!s.showEmptyStatuses} onClick={() => set({ showEmptyStatuses: !s.showEmptyStatuses })} />
              <MenuItem label="Show closed tasks" checked={s.showClosed} onClick={() => set({ showClosed: !s.showClosed })} />
              <MenuItem label="Me mode" checked={s.meMode} onClick={() => set({ meMode: !s.meMode })} />
              {features.sort && (
                <>
                  <MenuSeparator />
                  <MenuItem
                    icon={<ArrowDownUp size={15} />}
                    label="Sort"
                    submenu={(close) => (
                      <Menu style={{ width: 220 }}>
                        {BUILTIN_FIELDS.filter((f) => f.sortable).map((f) => {
                          const cur = s.sort.find((x) => x.field === f.id);
                          return (
                            <MenuItem
                              key={f.id}
                              label={f.label}
                              hint={cur ? (cur.dir === 'asc' ? '↑' : '↓') : undefined}
                              onClick={() => {
                                set({ sort: cur ? (cur.dir === 'asc' ? [{ field: f.id, dir: 'desc' }] : []) : [{ field: f.id, dir: 'asc' }] });
                                close();
                              }}
                            />
                          );
                        })}
                      </Menu>
                    )}
                  />
                </>
              )}
            </Menu>
          )}
        >
          <Tooltip label="Customize">
            <button className="toolbar-btn">
              <Settings2 size={15} />
            </button>
          </Tooltip>
        </Popover>
        <div className="split-btn add-task-btn">
          <button className="btn light sm" onClick={onAddTask}>
            <Plus size={14} /> Task
          </button>
          <button className="btn light sm" onClick={onAddTask}>
            <ChevronDown size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
